import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Search, SlidersHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { KeyboardEvent } from "react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CompaniesTable } from "@/components/companies/companies-table";
import { getCompaniesListServer } from "@/server/companies";
import type { CompanyListWithPagination } from "@/server/companies";
import type { CompanyResponse, CompanySearchLeadReviewResponse } from "@/lib/api/generated";
import { logError } from "@/lib/structured-log";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import CompanyDetailSheet from "@/components/companies/detail/sheet";
import { listCountriesServer } from "@/server/companies";
import { toast } from "sonner";
import { InlineSvg } from "@/components/ui/inline-svg";
import locationIcon from "@/assets/icons/location.svg?raw";

export const Route = createFileRoute("/workspace/$workspaceSlug/companies")({
  component: CompaniesPage,
  loader: async ({ context, parentMatchPromise }) => {
    const workspaceId = await getWorkspaceIdWithFallback(context, "companies", parentMatchPromise);

    try {
      // Load initial data from API
      const initialData = await getCompaniesListServer({
        data: {
          workspaceId,
          page: 1,
          pageSize: 20,
        },
      });

      // Load countries for filter options via BFF (graceful fallback)
      let countries: string[] = [];
      try {
        countries = await listCountriesServer({ data: { workspaceId } });
      } catch (error) {
        logError("companies.loader.filters.error", {
          error: error instanceof Error ? error.message : String(error),
          workspaceId,
        });
        countries = [];
      }

      return {
        workspaceId,
        initialData,
        countries,
      };
    } catch (error) {
      logError("companies.loader.error", {
        error: error instanceof Error ? error.message : String(error),
        workspaceId,
      });
      // Return empty data on error
      return {
        workspaceId,
        initialData: {
          companies: [],
          total: 0,
          limit: 20,
          offset: 0,
          page: 1,
          total_pages: 0,
        },
        countries: [],
      };
    }
  },
});

type SelectedCompany = CompanyResponse & {
  campaign_id?: string | null;
  review?: CompanySearchLeadReviewResponse;
};

function CompaniesPage() {
  const { t } = useTranslation(["pages", "common"]);
  const { workspaceId, initialData, countries } = Route.useLoaderData();

  // Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [country, setCountry] = useState<string | undefined>(undefined);
  const hasActiveFilters = Boolean(country);

  // Pagination states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Data states
  const [data, setData] = useState<CompanyListWithPagination | null>(initialData);
  const [loading, setLoading] = useState(false);

  // Sheet states
  const [selectedCompany, setSelectedCompany] = useState<SelectedCompany | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Handle row click
  const handleRowClick = (company: CompanyResponse) => {
    setSelectedCompany(company as SelectedCompany);
    setSheetOpen(true);
  };

  // Search handler
  const handleSearch = async () => {
    if (!workspaceId || loading) return;
    setLoading(true);
    try {
      const result = await getCompaniesListServer({
        data: {
          workspaceId,
          country,
          searchQuery: searchQuery || undefined,
          page,
          pageSize,
        },
      });

      setData(result);
    } catch (error) {
      logError("companies.list.fetch.error", {
        error: error instanceof Error ? error.message : String(error),
      });
      toast.error(t("pages:companies.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  // Page change handler
  const handlePageChange = async (nextPage: number) => {
    if (!workspaceId || loading) return;
    setLoading(true);
    setPage(nextPage);

    try {
      const result = await getCompaniesListServer({
        data: {
          workspaceId,
          country,
          searchQuery: searchQuery || undefined,
          page: nextPage,
          pageSize,
        },
      });

      setData(result);
    } catch (error) {
      logError("companies.list.page.error", {
        error: error instanceof Error ? error.message : String(error),
      });
      toast.error(t("pages:companies.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  // Page size change handler
  const handlePageSizeChange = async (nextSize: number) => {
    if (!workspaceId || loading) return;
    setLoading(true);
    setPageSize(nextSize);
    setPage(1);

    try {
      const result = await getCompaniesListServer({
        data: {
          workspaceId,
          country,
          searchQuery: searchQuery || undefined,
          page: 1,
          pageSize: nextSize,
        },
      });

      setData(result);
    } catch (error) {
      logError("companies.list.pagesize.error", {
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      void handleSearch();
    }
  };

  return (
    <div className="flex w-full h-full flex-col bg-white">
      <div className="h-[64px] px-6">
        <div className="flex h-full min-w-0 items-center gap-6">
          <h1 className="shrink-0 text-[16px] font-semibold text-gray-900">
            {t("pages:companies.title")}
          </h1>
          <div className="ml-auto flex h-10 min-w-[260px] max-w-[680px] flex-1 items-center gap-3 rounded-full border border-transparent bg-muted/60 px-3 transition-colors focus-within:border-blue-500">
            <Search className="size-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <Input
                className="h-8 min-w-0 border-0 bg-transparent px-0 text-sm shadow-none placeholder:text-muted-foreground focus-visible:ring-0"
                placeholder={t("pages:companies.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
            </div>
            {loading ? (
              <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
            ) : null}
            <Tooltip>
              <DropdownMenu>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="relative size-8 shrink-0 rounded-full bg-muted-foreground/10 text-foreground hover:bg-muted-foreground/15"
                      aria-label={t("pages:common.tooltips.showSearchOptions")}
                    >
                      <SlidersHorizontal className="size-4" />
                      {hasActiveFilters ? (
                        <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-blue-500" />
                      ) : null}
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <DropdownMenuContent align="end" sideOffset={10} className="w-72 p-3">
                  <div className="space-y-2">
                    <DropdownMenuLabel className="px-0 text-xs font-semibold text-muted-foreground">
                      {t("pages:companies.filters.country")}
                    </DropdownMenuLabel>
                    <Select
                      value={country ?? "all"}
                      onValueChange={(v) => setCountry(v === "all" ? undefined : v)}
                    >
                      <SelectTrigger className="h-10 w-full rounded-[12px] bg-background shadow-none">
                        <div className="flex min-w-0 items-center gap-2">
                          <InlineSvg content={locationIcon} />
                          <SelectValue placeholder={t("pages:companies.filters.country")} />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">
                          {t("pages:companies.filters.allCountries")}
                        </SelectItem>
                        {countries.map((cnt) => (
                          <SelectItem key={cnt} value={cnt}>
                            {cnt}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
              <TooltipContent side="bottom" align="end">
                {t("pages:common.tooltips.showSearchOptions")}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
      <Separator className="bg-[#dfdfdf]" />
      <Tabs
        defaultValue="table"
        className="flex h-full w-full flex-1 flex-col overflow-hidden gap-0"
      >
        <div className="flex flex-1 overflow-hidden">
          <TabsContent className="flex h-full flex-1 overflow-hidden relative" value="table">
            <CompaniesTable
              companies={data?.companies ?? []}
              loading={loading}
              totalCount={data?.total ?? 0}
              currentPage={page}
              initialPageSize={pageSize}
              pageCount={data?.total_pages ?? 1}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              onRowClick={handleRowClick}
            />
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/40">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
          </TabsContent>
        </div>
      </Tabs>

      <CompanyDetailSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        company={selectedCompany}
        workspaceId={workspaceId}
        campaignId={selectedCompany?.campaign_id ?? undefined}
        review={selectedCompany?.review ?? null}
      />
    </div>
  );
}
